import type { FC } from 'react'

import { Select } from '@skytech/ui'
import { twJoin } from 'tailwind-merge'

import { useTranslation } from '@/app/translations/translations-hooks'
import { useSearchParamsByKey } from '@/hooks/searchParams'
import useUser from '@/hooks/user'

export const getQueryFilterByCustomerId = (
  queryCustomer: string | null,
  customerId?: number | null
) => {
  if (!queryCustomer || queryCustomer === 'all' || queryCustomer === 'global') {
    return true
  } else {
    const id = Number(queryCustomer)
    return Number.isInteger(id) && customerId === id
  }
}

export const CustomerSelect: FC<{
  filterData: { customerId: number | null; customerName: string | null }[]
  isLoading: boolean
  defaultValueId?: number | 'all' | 'global'
  hasGlobalFilter?: boolean
  className?: string
}> = ({ filterData, isLoading, defaultValueId = 'all', hasGlobalFilter = false, className }) => {
  const translate = useTranslation()
  const user = useUser()
  if (!user) throw new Error('No user found!')
  const [queryCustomer, setQueryCustomer] = useSearchParamsByKey('company')

  const isAllAssetsFromUserConcern = filterData?.every(item => item.customerId === user.customerId)
  if (isAllAssetsFromUserConcern) {
    return null
  }

  // Get list of unique customers in list of assets
  const uniqueCustomers = Array.from(
    new Map(
      (filterData || []).map(item => [
        item.customerId,
        { id: item.customerId!, name: item.customerName! },
      ])
    ).values()
  )

  const customerOptions = [
    { value: 'all', label: translate('All companies') },
    ...(hasGlobalFilter ? [{ value: 'global', label: translate('Global') }] : []),
    ...(uniqueCustomers?.map(({ id, name }: { id: number; name: string }) => ({
      value: id.toString(),
      label: name,
    })) || []),
  ]

  return (
    <Select
      id="asset-customer"
      className={twJoin('min-w-44', className)}
      value={queryCustomer || defaultValueId?.toString()}
      options={customerOptions}
      isLoading={isLoading}
      onChange={option => setQueryCustomer(option?.value || '')}
    />
  )
}
